// A signed-in person with no platform role opened the HQ console (#475 rule 2, acceptance criterion
// 2): say so plainly, and send them to the venue console, where their work is.
//
// The venue console is another origin (`https://app.{domain}`, spec/14). Its host is the brand
// configuration's `domains.app` (`GET /v1/meta`, the one place a web client reads runtime brand values
// from, spec/03 §12), on this page's own scheme — `https` wherever the platform is deployed, `http` on
// a developer's machine. If the brand cannot be read, the words stand without the link.

import { useTranslation } from '@clavian/i18n/react';
import { Button } from '@clavian/ui-web';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import { useApi } from '../api/client';
import { queryKeys } from '../api/queries';
import { Mark } from '../shell/Mark';

/** `https://app.clavian.io/`, from the brand's `domains.app`; `undefined` if it cannot be read. */
function useVenueConsoleUrl(): string | undefined {
  const api = useApi();
  const meta = useQuery({ queryKey: queryKeys.meta, queryFn: () => api.getMeta(), retry: false, staleTime: 300_000 });
  const host: unknown = (meta.data as { brand?: { domains?: { app?: unknown } } } | undefined)?.brand?.domains?.app;
  if (typeof host !== 'string' || !/^[A-Za-z0-9.-]+(?::\d+)?$/.test(host)) return undefined;
  const scheme = typeof window === 'undefined' ? 'https:' : window.location.protocol;
  return `${scheme === 'http:' ? 'http:' : 'https:'}//${host}/`;
}

export function NotForYourAccount() {
  const { t } = useTranslation('hq');
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const consoleUrl = useVenueConsoleUrl();

  const signOut = async () => {
    try {
      await api.signOut();
    } finally {
      queryClient.clear();
      await navigate({ to: '/sign-in' });
    }
  };

  return (
    <main className="console-centred" id="console-main">
      <Mark size={40} />
      <h1>{t('notForYou.title')}</h1>
      <p>{t('notForYou.body')}</p>
      {consoleUrl === undefined ? null : (
        <p>
          <a href={consoleUrl}>{t('notForYou.toConsole')}</a>
        </p>
      )}
      <p>
        <Button variant="ghost" onClick={() => void signOut()}>
          {t('notForYou.signOut')}
        </Button>
      </p>
    </main>
  );
}
